import { useId } from 'react';
import type { Environment } from '../../api';
import { environmentKindLabel, humanize } from '../../labels';
import { Select } from '../primitives';

/**
 * EnvironmentSelector is the project's persistent context control. Two or
 * three environments read best as a segmented switch that shows every
 * choice at once; beyond that a select stays compact and keyboard-friendly
 * instead of wrapping into a second row of pills.
 */
export function EnvironmentSelector({
  environments,
  selectedId,
  onSelect,
}: {
  environments: Environment[];
  selectedId: string | null;
  onSelect: (environmentId: string) => void;
}) {
  const selectId = useId();
  if (environments.length === 0) return null;

  if (environments.length > 4) {
    return (
      <div className="env-selector env-selector--select">
        <label className="env-selector__label" htmlFor={selectId}>
          Environment
        </label>
        <Select
          id={selectId}
          value={selectedId ?? ''}
          onChange={(event) => onSelect(event.target.value)}
          data-testid="environment-select"
        >
          {environments.map((environment) => (
            <option key={environment.id} value={environment.id}>
              {environment.name} — {environmentKindLabel[environment.kind] ?? humanize(environment.kind)}
            </option>
          ))}
        </Select>
      </div>
    );
  }

  return (
    <div className="env-selector" role="group" aria-label="Environment" data-testid="environment-selector">
      {environments.map((environment) => {
        const selected = environment.id === selectedId;
        return (
          <button
            key={environment.id}
            type="button"
            aria-pressed={selected}
            className={`env-selector__option${selected ? ' env-selector__option--selected' : ''}`}
            onClick={() => onSelect(environment.id)}
          >
            <span className={`env-selector__mark env-selector__mark--${environment.kind}`} aria-hidden="true" />
            {environment.name}
            <span className="visually-hidden">
              {' '}
              ({environmentKindLabel[environment.kind] ?? humanize(environment.kind)})
            </span>
          </button>
        );
      })}
    </div>
  );
}
